import { FC, useEffect, useMemo, useRef, useState } from "react"
import { KDropdown, KSpan, KTooltip } from "kahuna-base-react-components"
import { KSelectOption } from "kahuna-base-react-components/dist/components/KDropdown/KDropdown"
import { handleAuthorizedRequest, trends } from "../../../../apis"
import { formatUsd, mapToCamelCase } from "../../../../utility"
import { TrackEarning, TrackEarningsData, TrackEarningsTotals } from "../../../../types"
import { chartLabels, platformIcons } from "../../../../constants"
import { lang } from "../../../../constants/languages"
import Card from "../../analysis-new/components/Card"
import EarningsTable from "./EarningsTable"
import Loading from "../../../ui/Loading"
//@ts-ignore
import InfoCircle from "../../../../assets/analytics_icons/info-circle.svg"

const LIMIT = 10
const EMPTY_TOTALS: TrackEarningsTotals = { totalNetUsd: 0, totalAmountUsd: 0, totalStream: 0 }

export type EarningsProps = {
  isAdmin: boolean
  selectedUser?: KSelectOption
  selectedAccount?: KSelectOption
}

// Royalty data is stored per month, so periods are YYYYMM strings rather than dates.
export const formatYYYYMM = (yyyymm: string) => {
  if (!yyyymm || yyyymm.length < 6) return yyyymm || ""
  const monthNames: { [key: number]: string } = lang.common.months_short
  return `${monthNames[parseInt(yyyymm.substring(4, 6), 10) - 1]} ${yyyymm.substring(0, 4)}`
}

const Earnings: FC<EarningsProps> = ({ isAdmin, selectedUser, selectedAccount }) => {
  const [trackEarnings, setTrackEarnings] = useState<TrackEarning[]>([])
  const [totals, setTotals] = useState<TrackEarningsTotals>(EMPTY_TOTALS)
  const [count, setCount] = useState<number>(0)
  const [dateOptions, setDateOptions] = useState<string[]>([])
  const [platformOptions, setPlatformOptions] = useState<string[]>([])
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>([])
  const [startDate, setStartDate] = useState<string>("")
  const [endDate, setEndDate] = useState<string>("")
  const [pageNumber, setPageNumber] = useState<number>(1)
  const [loading, setLoading] = useState(false)
  const lastOptionsKeyRef = useRef<string>("")
  const lastRequestKeyRef = useRef<string>("")

  // The admin page reads one selected artist; artists themselves switch between their own accounts.
  const scopeParams = useMemo(() => {
    const userIdParam = isAdmin && selectedUser?.value ? `&user_id=${selectedUser.value}` : ""
    const accountParam = selectedAccount?.value2 ? `&selected_account=${selectedAccount.value2}` : ""
    return `${userIdParam}${accountParam}`
  }, [isAdmin, selectedUser, selectedAccount])

  const waitingForUser = isAdmin && !selectedUser?.value

  const fetchOptions = async () => {
    setLoading(true)
    try {
      await handleAuthorizedRequest(async () => {
        const response = await trends(`/track_earnings/?fetch_options=1&limit=1${scopeParams}`, "get")
        if (!response?.data) return

        const data: TrackEarningsData = mapToCamelCase(response.data)
        const dates = data?.options?.dateOptions || []
        setDateOptions(dates)
        setPlatformOptions(data?.options?.platformOptions || [])
        setSelectedPlatforms([])
        setPageNumber(1)
        // Royalty statements arrive with a delay, so open on the most recent period that has data.
        setStartDate(dates[0] || "")
        setEndDate(dates[0] || "")
        if (!dates.length) {
          setTrackEarnings([])
          setCount(0)
          setTotals(EMPTY_TOTALS)
        }
      }, "")
    } finally {
      setLoading(false)
    }
  }

  const fetchEarnings = async () => {
    setLoading(true)
    try {
      await handleAuthorizedRequest(async () => {
        const platformParam = selectedPlatforms.length > 0 ? `&platforms=${selectedPlatforms.join(",")}` : ""
        const offset = (pageNumber - 1) * LIMIT
        const response = await trends(
          `/track_earnings/?dates=${startDate},${endDate}${platformParam}${scopeParams}&limit=${LIMIT}&offset=${offset}`,
          "get"
        )
        if (!response?.data) return

        const data: TrackEarningsData = mapToCamelCase(response.data)
        setTrackEarnings(data?.results || [])
        setCount(data?.count || 0)
        setTotals(data?.totals || EMPTY_TOTALS)
      }, "")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (waitingForUser) return

    const optionsKey = scopeParams
    if (lastOptionsKeyRef.current === optionsKey) return
    lastOptionsKeyRef.current = optionsKey

    fetchOptions()
  }, [scopeParams, waitingForUser])

  useEffect(() => {
    if (waitingForUser) return
    if (!(startDate && endDate)) return

    const key = `${startDate}_${endDate}__p:${[...selectedPlatforms].sort().join("|")}__s:${scopeParams}__pn:${pageNumber}`
    if (key === lastRequestKeyRef.current) return

    const currentBaseKey = key.split("__pn:")[0]
    const lastBaseKey = lastRequestKeyRef.current ? lastRequestKeyRef.current.split("__pn:")[0] : ""

    if (currentBaseKey !== lastBaseKey && pageNumber !== 1) {
      setPageNumber(1)
      return
    }

    lastRequestKeyRef.current = key
    fetchEarnings()
  }, [startDate, endDate, selectedPlatforms, pageNumber, scopeParams, waitingForUser])

  const monthOptions = (values: string[]) =>
    values.map((value, index) => ({ label: formatYYYYMM(value), value: index, value2: value }))

  const startDateOptions = useMemo(
    () => monthOptions(endDate ? dateOptions.filter((date) => date <= endDate) : dateOptions),
    [dateOptions, endDate]
  )

  const endDateOptions = useMemo(
    () => monthOptions(startDate ? dateOptions.filter((date) => date >= startDate) : dateOptions),
    [dateOptions, startDate]
  )

  const platformDropdownOptions = useMemo(
    () =>
      platformOptions.map((platform, index) => ({
        //@ts-ignore
        label: chartLabels.platform[platform] || platform,
        value: index,
        value2: platform,
        //@ts-ignore
        icon: platformIcons[platform]
      })),
    [platformOptions]
  )

  const renderFilters = () => (
    <div className="flex flex-row gap-5 items-center justify-between flex-wrap">
      <div className="flex flex-row items-center gap-3 flex-1 flex-wrap">
        <div className="flex flex-col gap-1 justify-center items-start">
          <div className="flex flex-row items-center gap-1">
            <KSpan text={lang.royalties.earnings.period} color="#111" fontWeight={500} />
            <KTooltip
              padding="8px"
              position="bottom"
              marginLeft="-20px"
              content={
                <div className="h-auto w-[300px] flex items-start justify-center flex-col">
                  <KSpan
                    text={lang.royalties.earnings.monthly_data_tooltip}
                    fontSize={12}
                    lineHeight="16px"
                    color="#111"
                  />
                </div>
              }
            >
              <img src={InfoCircle} className="w-4 h-4 min-w-4 min-h-4" alt="period-info-icon" />
            </KTooltip>
          </div>
          <div className="flex flex-row items-center gap-2">
            <KDropdown
              width={140}
              rightIcon="/analytics_icons/caret-down-new.svg"
              enableIndicator={true}
              options={startDateOptions}
              onSelect={(selected) => {
                setStartDate((selected as KSelectOption)?.value2 || "")
                setPageNumber(1)
              }}
              selected={startDateOptions.find((option) => option.value2 === startDate)}
            />
            <KSpan text="-" color="#111" />
            <KDropdown
              width={140}
              rightIcon="/analytics_icons/caret-down-new.svg"
              enableIndicator={true}
              options={endDateOptions}
              onSelect={(selected) => {
                setEndDate((selected as KSelectOption)?.value2 || "")
                setPageNumber(1)
              }}
              selected={endDateOptions.find((option) => option.value2 === endDate)}
            />
          </div>
        </div>
        <div className="flex flex-col gap-1 justify-center items-start">
          <KSpan text={lang.royalties.earnings.platforms} color="#111" fontWeight={500} />
          <KDropdown
            isClearable={true}
            isMulti={true}
            showOnlyIconsInMulti={true}
            width={200}
            closeMenuOnSelect={false}
            options={platformDropdownOptions}
            onSelect={() => {}}
            onBlur={(selected) => {
              if (!selected) return
              setSelectedPlatforms((selected as KSelectOption[])?.map((entity) => entity.value2 || "") || [])
              setPageNumber(1)
            }}
            placeholder={lang.royalties.earnings.all}
            selected={platformDropdownOptions.filter(
              (option) => option?.value2 && selectedPlatforms.includes(option?.value2)
            )}
            sortSelectedFirst={true}
          />
        </div>
      </div>
    </div>
  )

  if (waitingForUser) {
    return (
      <div className="pl-3 mt-6">
        <KSpan text={lang.royalties.earnings.select_user} lineHeight="20px" />
      </div>
    )
  }

  if (loading) return <Loading />

  return (
    <div className="flex flex-col gap-5 mt-6">
      {renderFilters()}
      <div className="flex gap-3 flex-wrap">
        <div className="flex min-w-[240px] basis-0 flex-1">
          <Card
            title={lang.royalties.earnings.total_earnings}
            value={formatUsd(totals.totalNetUsd)}
            icon="/analytics_icons/money-cash.svg"
            tooltipContent={lang.royalties.earnings.monthly_data_tooltip}
          />
        </div>
        <div className="flex min-w-[240px] basis-0 flex-1">
          <Card
            title={lang.royalties.earnings.total_streams}
            value={(totals.totalStream || 0).toLocaleString("en-US")}
            icon="/analytics_icons/headphones.svg"
          />
        </div>
      </div>
      <div className="flex gap-3">
        <div
          style={{
            borderRadius: "10px",
            paddingBottom: 10,
            border: "1px solid var(--stroke-soft-200, #F3F3F3)",
            background: " var(--bg-white-0, #FFF)",
            boxShadow: "0px 2px 4px 0px rgba(27, 28, 29, 0.04)"
          }}
          className="flex min-w-0 basis-0 flex-1 h-full items-center justify-center"
        >
          <EarningsTable
            trackEarnings={trackEarnings}
            count={count}
            pageNumber={pageNumber}
            setPageNumber={setPageNumber}
            limit={LIMIT}
          />
        </div>
      </div>
    </div>
  )
}

export default Earnings
